"use client";

import { FC, useState, TouchEvent, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import ArrowLeft from "@mui/icons-material/ArrowLeft";
import ArrowRight from "@mui/icons-material/ArrowRight";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { Image } from "@/types/thread";
import Toolbar from "./Toolbar";
import ZoomImage from "./ZoomImage";
import DesktopCarousel from "./DesktopCarousel";

const MAX_DISPLAYED = 5;
const MIN_SWIPE_DISTANCE = 50;

interface ImageViewerProps {
  images: Image[];
  onClose: () => void;
  initialSelection?: number;
  roomUUID?: string;
}

const ImageViewer: FC<ImageViewerProps> = ({
  images,
  onClose,
  initialSelection = 0,
  roomUUID,
}) => {
  const { classes, cx } = useStyles();
  const [selectedImage, setSelectedImage] = useState(initialSelection);
  const [showCaption, setShowCaption] = useState(false);
  const [touchStart, setTouchStart] = useState<Nullable<number>>(null);
  const [touchEnd, setTouchEnd] = useState<Nullable<number>>(null);

  const handleNext = () => {
    if (selectedImage === images.length - 1) {
      setSelectedImage(0);
    } else {
      setSelectedImage(selectedImage + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedImage === 0) {
      setSelectedImage(images.length - 1);
    } else {
      setSelectedImage(selectedImage - 1);
    }
  };

  const handleSelect = (newSelectionIndex: number) => {
    if (images.length <= MAX_DISPLAYED) {
      setSelectedImage(newSelectionIndex);
    } else {
      let newIndex = selectedImage + newSelectionIndex;
      if (selectedImage + newSelectionIndex > length - 1) {
        newIndex = selectedImage + newSelectionIndex - length;
      }
      setSelectedImage(newIndex);
    }
  };

  const toggleShowCaption = () => {
    setShowCaption((prevValue) => !prevValue);
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  const activeImages =
    images.length <= MAX_DISPLAYED
      ? images.slice()
      : images.slice(selectedImage, selectedImage + MAX_DISPLAYED);
  const displayImages =
    activeImages.length <= MAX_DISPLAYED && images.length > MAX_DISPLAYED
      ? [
          ...activeImages,
          ...images.slice(0, MAX_DISPLAYED - activeImages.length),
        ]
      : activeImages;

  useEffect(() => {
    console.info({ imagesLength: images.length, selectedImage });
    if (images.length < selectedImage + 1) {
      setSelectedImage(0);
    }
  }, [images, selectedImage]);

  useEffect(() => {
    if (selectedImage !== initialSelection) {
      setSelectedImage(initialSelection);
    }
  }, [initialSelection]);

  if (images.length === 0) {
    return null;
  }

  return (
    <Modal open className={classes.modal}>
      <div className={classes.content} onClick={(e) => e.stopPropagation()}>
        <Toolbar
          selectedImage={images[selectedImage]}
          onClose={onClose}
          showCaption={showCaption}
          toggleShowCaption={toggleShowCaption}
          roomUUID={roomUUID}
        />
        <div
          className={classes.featuredImageSection}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <ZoomImage image={images[selectedImage]} />
          {images.length > 1 && (
            <>
              <ArrowLeft
                className={cx(classes.arrowButton, classes.arrowLeft)}
                onClick={handlePrevious}
              />
              <ArrowRight
                className={cx(classes.arrowButton, classes.arrowRight)}
                onClick={handleNext}
              />
            </>
          )}
          {images.length > 1 && (
            <div className={classes.mobileStepperContainer}>
              {[...new Array(images.length)].map((_, index) => (
                <div
                  key={`mobile-stepper-${index + 1}`}
                  className={cx(
                    classes.mobileStepperDot,
                    index === selectedImage && classes.mobileStepperDotActive
                  )}
                />
              ))}
            </div>
          )}
          {showCaption && (
            <div className={classes.captionOverlay}>
              <Typography variant="body1">
                {images[selectedImage].alt}
              </Typography>
            </div>
          )}
        </div>
        {images.length > 1 && (
          <DesktopCarousel
            images={displayImages}
            selectedIndex={selectedImage}
            onSelect={handleSelect}
          />
        )}
      </div>
    </Modal>
  );
};

export default ImageViewer;
